import { useEffect, useRef, useState } from 'react';

import { CheckIcon } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import CopyIcon from 'icons/Copy';
import type { SharepointWriteStatus } from 'pages/QuoteView/LoadBuilding/sharepoint/utils/writeSharepointLoadIdToWorkbook';
import { Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { copyToClipboard } from 'utils/copyToClipboard';
import { cn } from 'utils/shadcn';

function SharepointWritebackCopy({
  status,
}: {
  status: SharepointWriteStatus;
}) {
  switch (status.phase) {
    case 'pending':
      return (
        <Typography variant='body' className='mt-2 text-neutral-700'>
          Updating {status.targetLabel}...
        </Typography>
      );
    case 'success':
      return (
        <Typography variant='body' className='mt-2 text-neutral-700'>
          Your load ID was written to {status.targetLabel}.
        </Typography>
      );
    case 'error':
      return (
        <Typography variant='body' className='mt-2 text-neutral-700'>
          Could not update {status.targetLabel} automatically. Please paste the
          load ID there manually.
        </Typography>
      );
  }
}

export type SharepointAgileCreatedSummary = {
  bookingNumber: string;
  poNumber: string;
};

/** @deprecated Next-suggestion hint removed from banner; type kept for callers that still compute next-item metadata. */
export type NextLoadSuggestionSummary = {
  poNumber: string;
  bookingNumber: string;
};

export interface LoadCreatedMessageProps {
  builtLoadId: Undef<string>;
  /** Overrides default "Load ID #:" / McLeod label (e.g. "BOL:" for MG-Boomi). */
  builtLoadIdLabel?: string;
  /** PO for the load that was just created (preferred over SharePoint summary PO when both exist). */
  poNumber?: string;
  /** SharePoint Agile: expanded copy + paste-into-sheet hint; McLeod Load ID label. */
  sharepointAgileCreated?: SharepointAgileCreatedSummary;
  /** SharePoint Graph writeback status shown inside this banner. */
  sharepointWriteStatus?: Undef<SharepointWriteStatus>;
}

export const LoadCreatedMessage = ({
  builtLoadId,
  builtLoadIdLabel,
  poNumber,
  sharepointAgileCreated,
  sharepointWriteStatus,
}: LoadCreatedMessageProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const loadCreatedRef = useRef<HTMLDivElement>(null);

  const poNumberDisplayText =
    poNumber?.trim() || sharepointAgileCreated?.poNumber?.trim() || '';
  const hasDisplayablePONumber = poNumberDisplayText.length > 0;
  const loadIdLabel =
    builtLoadIdLabel ??
    (sharepointAgileCreated ? 'McLeod Load ID:' : 'Load ID #:');
  const copyLoadIdTitle = (() => {
    if (sharepointAgileCreated) {
      return 'Copy McLeod Load ID';
    }
    if (builtLoadIdLabel) {
      return `Copy ${builtLoadIdLabel.replace(/:$/, '')}`;
    }
    return 'Copy Load ID';
  })();

  const handleCopyLoadId = async (content: string) => {
    if (content) {
      try {
        await copyToClipboard(content, {
          showToast: false,
        });
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy load ID:', err);
      }
    }
  };

  useEffect(() => {
    if (builtLoadId && loadCreatedRef.current) {
      setTimeout(() => {
        loadCreatedRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [builtLoadId]);

  const copyLoadIdButton = (
    <Button
      onClick={() => handleCopyLoadId(builtLoadId!)}
      variant='ghost'
      type='button'
      buttonNamePosthog={ButtonNamePosthog.LoadBuildingCopyLoadID}
      className={cn(
        'p-1 transition-colors border-none',
        isCopied ? 'cursor-default' : 'cursor-pointer'
      )}
      title={copyLoadIdTitle}
    >
      {isCopied ? (
        <Tooltip open={true}>
          <TooltipTrigger asChild>
            <CheckIcon className='h-4 w-4 ' />
          </TooltipTrigger>
          <TooltipContent>
            <Typography variant='body-xs'>Copied!</Typography>
          </TooltipContent>
        </Tooltip>
      ) : (
        <CopyIcon className='h-4 w-4 text-neutral-500' />
      )}
    </Button>
  );

  return (
    <TooltipProvider>
      <Flex direction='col' justify='center' className='w-full'>
        {builtLoadId && (
          <>
            <div
              ref={loadCreatedRef}
              className='whitespace-pre-wrap my-3 rounded py-3 px-4 bg-success-200 w-full'
              data-testid='created-load-banner'
            >
              <Typography className='mb-2' weight='bold'>
                Load Created 🎉
              </Typography>
              {sharepointAgileCreated ? (
                <Flex direction='col' gap='sm' className='w-full'>
                  <Flex direction='row' align='center' gap='xs' wrap='wrap'>
                    <Typography variant='body' weight='bold'>
                      {loadIdLabel}{' '}
                    </Typography>
                    <Typography variant='body'>{builtLoadId}</Typography>
                    {copyLoadIdButton}
                  </Flex>
                  <Typography variant='body' className='text-neutral-700'>
                    Please paste new Load ID into sheet
                  </Typography>
                  <Flex direction='row' gap='xs' wrap='wrap'>
                    <Typography variant='body' weight='bold'>
                      Booking #:
                    </Typography>
                    <Typography variant='body'>
                      {sharepointAgileCreated.bookingNumber || '-'}
                    </Typography>
                  </Flex>
                  {hasDisplayablePONumber ? (
                    <Flex direction='row' gap='xs' wrap='wrap'>
                      <Typography variant='body' weight='bold'>
                        PO #:
                      </Typography>
                      <Typography variant='body'>
                        {poNumberDisplayText}
                      </Typography>
                    </Flex>
                  ) : null}
                  {sharepointWriteStatus ? (
                    <SharepointWritebackCopy status={sharepointWriteStatus} />
                  ) : null}
                </Flex>
              ) : (
                <>
                  <Flex direction='row' align='center' gap='xs' wrap='wrap'>
                    <Typography variant='body' weight='bold'>
                      {loadIdLabel}{' '}
                    </Typography>
                    <Typography variant='body'>{builtLoadId}</Typography>
                    {copyLoadIdButton}
                  </Flex>
                  {hasDisplayablePONumber ? (
                    <Flex direction='row' gap='xs' wrap='wrap' className='mt-2'>
                      <Typography variant='body' weight='bold'>
                        PO #:
                      </Typography>
                      <Typography variant='body'>
                        {poNumberDisplayText}
                      </Typography>
                    </Flex>
                  ) : null}
                  {sharepointWriteStatus ? (
                    <SharepointWritebackCopy status={sharepointWriteStatus} />
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
      </Flex>
    </TooltipProvider>
  );
};
